'use client';

import { useMemo } from 'react';
import { useRegions } from '@/hooks/useRegions';
import RegionCard from '@/components/RegionCard';
import QuickCompareRegions from '@/components/QuickCompareRegions';
import Breadcrumb from '@/components/Breadcrumb';

export default function LakesPage() {
  const { regions, loading, error } = useRegions('lake');

  // 计算最大面积用于比例显示
  const maxArea = useMemo(() => {
    return Math.max(...regions.map(r => r.area_km2));
  }, [regions]);

  // 处理比较
  const handleCompare = (region: any) => {
    const url = `/map?a=${region.slug}`;
    window.open(url, '_blank');
  };

  if (loading) {
    return (
      <div className="mx-auto max-w-7xl px-4 md:px-6 py-8">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900 mx-auto mb-4"></div>
            <p className="text-slate-600">Loading lakes...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto max-w-7xl px-4 md:px-6 py-8">
        <div className="text-center">
          <p className="text-red-600 mb-4">Error: {error}</p>
          <button 
            onClick={() => window.location.reload()}
            className="bg-slate-900 text-white px-4 py-2 rounded-md hover:bg-slate-800"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl px-4 md:px-6 py-8">
      <Breadcrumb items={[
        { label: 'Browse', href: '/browse' },
        { label: 'Lakes' }
      ]} />
      
      {/* 页面标题 */}
      <div className="mt-8 mb-8">
        <h1 className="text-3xl font-bold text-slate-900 mb-4">Lakes</h1>
        <p className="text-lg text-slate-600">
          Compare the true size of lakes and explore their geographical features.
        </p>
      </div>

      {/* 快速比较工具 */}
      <div className="mb-8">
        <QuickCompareRegions regions={regions} type="lake" />
      </div>

      {/* 湖泊列表 */}
      <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {regions.map((region) => (
          <RegionCard
            key={region.id}
            region={region}
            type="lake"
            onCompare={handleCompare}
            maxArea={maxArea}
          />
        ))}
      </div>

      {/* 关于湖泊真实大小的描述 */}
      <div className="mt-12 bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200 rounded-lg p-6">
        <h2 className="text-xl font-semibold text-slate-900 mb-4">Understanding True Size of Lakes</h2>
        <p className="text-slate-700 leading-relaxed">
          Lakes vary dramatically in size, from massive inland seas like the Caspian Sea to small mountain lakes. Traditional maps often fail to convey the true scale of these freshwater bodies. Our interactive platform reveals the genuine size of the world&apos;s major lakes, helping you understand their true geographical significance and compare their actual areas through our precise scale comparison tools.
        </p>
      </div>

      {/* 相关页面导航 */}
      <div className="mt-8">
        <h2 className="text-xl font-semibold text-slate-900 mb-4">More Comparisons</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          <a 
            href="/true-size-of-countries"
            className="inline-flex items-center justify-center px-3 py-2 bg-blue-50 hover:bg-blue-100 text-blue-700 hover:text-blue-800 rounded-lg font-medium transition-colors border border-blue-200 hover:border-blue-300 text-sm"
          >
            True Size of Countries
          </a>
          <a 
            href="/true-size-of-continents"
            className="inline-flex items-center justify-center px-3 py-2 bg-purple-50 hover:bg-purple-100 text-purple-700 hover:text-purple-800 rounded-lg font-medium transition-colors border border-purple-200 hover:border-purple-300 text-sm"
          >
            True Size of Continents
          </a>
          <a 
            href="/true-size-of-territories"
            className="inline-flex items-center justify-center px-3 py-2 bg-green-50 hover:bg-green-100 text-green-700 hover:text-green-800 rounded-lg font-medium transition-colors border border-green-200 hover:border-green-300 text-sm"
          >
            True Size of Territories
          </a>
          <a 
            href="/true-size-of-oceans"
            className="inline-flex items-center justify-center px-3 py-2 bg-cyan-50 hover:bg-cyan-100 text-cyan-700 hover:text-cyan-800 rounded-lg font-medium transition-colors border border-cyan-200 hover:border-cyan-300 text-sm"
          >
            True Size of Oceans
          </a>
          <a 
            href="/true-size-of-islands"
            className="inline-flex items-center justify-center px-3 py-2 bg-orange-50 hover:bg-orange-100 text-orange-700 hover:text-orange-800 rounded-lg font-medium transition-colors border border-orange-200 hover:border-orange-300 text-sm"
          >
            True Size of Islands
          </a>
        </div>
      </div>

    </div>
  );
}
